/**
 * Publish Dialog - Multi-stage deployment UI
 * 
 * Shows step-by-step progress with validation, security check, and deployment stages
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Steps,
  Button,
  Typography,
  Space,
  Alert,
  Card,
  Divider,
  Tag,
  Spin
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  FileTextOutlined,
  SafetyOutlined,
  RocketOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { apiService, PluginDraft as ApiPluginDraft } from '../services/apiService';

const { Title, Text, Paragraph } = Typography;

// Use the same PluginDraft type from apiService to avoid type conflicts
type PluginDraft = ApiPluginDraft;

interface PublishStage {
  key: string;
  title: string;
  icon: React.ReactNode;
  status: 'wait' | 'process' | 'finish' | 'error';
  description?: string;
  logs?: string[];
  error?: string;
  retryable?: boolean;
}

interface PublishDialogProps {
  visible: boolean;
  draft: PluginDraft | null;
  onClose: () => void;
  onSuccess: () => void;
}

const PublishDialog: React.FC<PublishDialogProps> = ({
  visible,
  draft,
  onClose,
  onSuccess
}) => {
  const [stages, setStages] = useState<PublishStage[]>([
    {
      key: 'validation',
      title: 'Validation',
      icon: <FileTextOutlined />,
      status: 'wait',
      description: 'Validating plugin manifest and bundle'
    },
    {
      key: 'security',
      title: 'Security Check',
      icon: <SafetyOutlined />,
      status: 'wait',
      description: 'Scanning for security vulnerabilities'
    },
    {
      key: 'deploy',
      title: 'Deployment',
      icon: <RocketOutlined />,
      status: 'wait',
      description: 'Writing bundle and updating configuration'
    }
  ]);

  const [publishing, setPublishing] = useState(false);
  const [currentStage, setCurrentStage] = useState<string | null>(null);

  // Reset when dialog opens/closes
  useEffect(() => {
    if (visible && draft) {
      resetStages();
    } else {
      setPublishing(false);
      setCurrentStage(null);
    }
  }, [visible, draft]);

  const resetStages = () => {
    setStages([
      {
        key: 'validation',
        title: 'Validation',
        icon: <FileTextOutlined />,
        status: 'wait',
        description: 'Validating plugin manifest and bundle'
      },
      {
        key: 'security',
        title: 'Security Check',
        icon: <SafetyOutlined />,
        status: 'wait',
        description: 'Scanning for security vulnerabilities'
      },
      {
        key: 'deploy',
        title: 'Deployment',
        icon: <RocketOutlined />,
        status: 'wait',
        description: 'Writing bundle and updating configuration'
      }
    ]);
    setCurrentStage(null);
  };

  const updateStage = (key: string, updates: Partial<PublishStage>) => {
    setStages(prev => prev.map(stage => 
      stage.key === key ? { ...stage, ...updates } : stage
    ));
  };

  const handlePublish = async () => {
    if (!draft) return;

    setPublishing(true);
    resetStages();

    try {
      // Stage 1: Validation
      setCurrentStage('validation');
      updateStage('validation', { 
        status: 'process',
        logs: ['Starting validation...']
      });

      try {
        const validationResult = await apiService.validateDraft(draft.draft_id);
        
        if (!validationResult.success) {
          throw new Error(validationResult.error || 'Validation failed');
        }

        updateStage('validation', {
          status: 'finish',
          logs: [
            ...(stages.find(s => s.key === 'validation')?.logs || []),
            '✓ Manifest structure valid',
            '✓ Bundle file found',
            '✓ Required fields present',
            '✓ Route contributions valid'
          ],
          description: 'Validation passed'
        });
      } catch (error: any) {
        updateStage('validation', {
          status: 'error',
          error: error.message || 'Validation failed',
          logs: [
            ...(stages.find(s => s.key === 'validation')?.logs || []),
            `✗ Error: ${error.message || 'Validation failed'}`
          ],
          retryable: true
        });
        setPublishing(false);
        setCurrentStage(null);
        return;
      }

      // Stage 2: Security Check
      setCurrentStage('security');
      updateStage('security', {
        status: 'process',
        logs: ['Running security scan...']
      });

      try {
        const securityResult = await apiService.checkDraftSecurity(draft.draft_id);
        
        if (!securityResult.success) {
          throw new Error(securityResult.error || 'Security check failed');
        }

        updateStage('security', {
          status: 'finish',
          logs: [
            ...(stages.find(s => s.key === 'security')?.logs || []),
            '✓ No dangerous API calls detected',
            '✓ No eval() or dynamic code execution',
            '✓ Permissions validated',
            '✓ Bundle integrity verified'
          ],
          description: 'Security check passed'
        });
      } catch (error: any) {
        updateStage('security', {
          status: 'error',
          error: error.message || 'Security check failed',
          logs: [
            ...(stages.find(s => s.key === 'security')?.logs || []),
            `✗ Error: ${error.message || 'Security check failed'}`
          ],
          retryable: true
        });
        setPublishing(false);
        setCurrentStage(null);
        return;
      }

      // Stage 3: Deployment
      setCurrentStage('deploy');
      updateStage('deploy', {
        status: 'process',
        logs: ['Starting deployment...']
      });

      try {
        const deployResult = await apiService.publishDraft(draft.draft_id);
        
        if (!deployResult.success) {
          throw new Error(deployResult.error || 'Deployment failed');
        }

        updateStage('deploy', {
          status: 'finish',
          logs: [
            ...(stages.find(s => s.key === 'deploy')?.logs || []),
            '✓ Bundle written to /app/plugins/',
            '✓ plugins.yaml updated',
            '✓ Registry refreshed',
            '✓ Plugin enabled'
          ],
          description: 'Deployment successful'
        });

        // Success - wait a moment then close
        setTimeout(() => {
          setPublishing(false);
          setCurrentStage(null);
          onSuccess();
          onClose();
        }, 1500);

      } catch (error: any) {
        updateStage('deploy', {
          status: 'error',
          error: error.message || 'Deployment failed',
          logs: [
            ...(stages.find(s => s.key === 'deploy')?.logs || []),
            `✗ Error: ${error.message || 'Deployment failed'}`
          ],
          retryable: true
        });
        setPublishing(false);
        setCurrentStage(null);
      }

    } catch (error: any) {
      console.error('Publish error:', error);
      setPublishing(false);
      setCurrentStage(null);
    }
  };

  const handleRetryStage = async (stageKey: string) => {
    if (!draft) return;

    // Find the stage index
    const stageIndex = stages.findIndex(s => s.key === stageKey);
    if (stageIndex === -1) return;

    // Reset all stages from this one onwards
    const newStages = [...stages];
    for (let i = stageIndex; i < newStages.length; i++) {
      newStages[i] = {
        ...newStages[i],
        status: i === stageIndex ? 'process' : 'wait',
        error: undefined,
        logs: i === stageIndex ? [`Retrying ${newStages[i].title}...`] : []
      };
    }
    setStages(newStages);
    setCurrentStage(stageKey);

    try {
      if (stageKey === 'validation') {
        const validationResult = await apiService.validateDraft(draft.draft_id);
        if (!validationResult.success) {
          throw new Error(validationResult.error || 'Validation failed');
        }
        updateStage('validation', {
          status: 'finish',
          logs: ['✓ Validation passed'],
          description: 'Validation passed'
        });
        // Continue to next stage
        handlePublish();
      } else if (stageKey === 'security') {
        const securityResult = await apiService.checkDraftSecurity(draft.draft_id);
        if (!securityResult.success) {
          throw new Error(securityResult.error || 'Security check failed');
        }
        updateStage('security', {
          status: 'finish',
          logs: ['✓ Security check passed'],
          description: 'Security check passed'
        });
        // Continue to next stage
        handlePublish();
      } else if (stageKey === 'deploy') {
        const deployResult = await apiService.publishDraft(draft.draft_id);
        if (!deployResult.success) {
          throw new Error(deployResult.error || 'Deployment failed');
        }
        updateStage('deploy', {
          status: 'finish',
          logs: ['✓ Deployment successful'],
          description: 'Deployment successful'
        });
        setTimeout(() => {
          setPublishing(false);
          setCurrentStage(null);
          onSuccess();
          onClose();
        }, 1500);
      }
    } catch (error: any) {
      updateStage(stageKey, {
        status: 'error',
        error: error.message || `${stageKey} failed`,
        logs: [
          ...(stages.find(s => s.key === stageKey)?.logs || []),
          `✗ Error: ${error.message || `${stageKey} failed`}`
        ],
        retryable: true
      });
      setPublishing(false);
      setCurrentStage(null);
    }
  };

  const getStageIcon = (stage: PublishStage) => {
    if (stage.status === 'process') {
      return <LoadingOutlined />;
    }
    if (stage.status === 'finish') {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    if (stage.status === 'error') {
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    }
    return stage.icon;
  };

  const currentStepIndex = stages.findIndex(s => s.status === 'process' || s.status === 'error');
  const allFinished = stages.every(s => s.status === 'finish');
  const hasError = stages.some(s => s.status === 'error');

  return (
    <Modal
      title={
        <Space>
          <RocketOutlined style={{ color: '#1890ff' }} />
          <span>Publish Plugin</span>
        </Space>
      }
      open={visible}
      onCancel={!publishing ? onClose : undefined}
      width={700}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={publishing && !hasError}>
          {hasError ? 'Close' : 'Cancel'}
        </Button>,
        ...(hasError ? [
          <Button
            key="retry"
            type="primary"
            onClick={() => {
              const failedStage = stages.find(s => s.status === 'error');
              if (failedStage) {
                handleRetryStage(failedStage.key);
              }
            }}
          >
            <ReloadOutlined /> Retry Failed Stage
          </Button>
        ] : []),
        ...(!publishing && !hasError ? [
          <Button key="publish" type="primary" onClick={handlePublish}>
            <RocketOutlined /> Start Publishing
          </Button>
        ] : [])
      ]}
      maskClosable={!publishing}
    >
      {draft && (
        <div>
          {/* Plugin Info */}
          <Card size="small" style={{ marginBottom: 24, backgroundColor: '#fafafa' }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text strong>{draft.name}</Text>
                  {draft.manifest?.version && <Tag style={{ marginLeft: 8 }}>v{draft.manifest.version}</Tag>}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Draft ID: {draft.draft_id}
                </Text>
              </Space>
          </Card>

          {/* Steps */}
          <Steps
            current={currentStepIndex >= 0 ? currentStepIndex : stages.length}
            status={hasError ? 'error' : allFinished ? 'finish' : 'process'}
            direction="vertical"
            size="small"
          >
            {stages.map((stage, index) => (
              <Steps.Step
                key={stage.key}
                title={
                  <Space>
                    {getStageIcon(stage)}
                    <span>{stage.title}</span>
                    {stage.status === 'error' && stage.retryable && (
                      <Button
                        type="link"
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => handleRetryStage(stage.key)}
                        style={{ padding: 0, height: 'auto' }}
                      >
                        Retry
                      </Button>
                    )}
                  </Space>
                }
                description={
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {stage.description}
                    </Text>
                    {stage.error && (
                      <Alert
                        message={stage.error}
                        type="error"
                        style={{ marginTop: 8, fontSize: '12px' }}
                        showIcon
                      />
                    )}
                    {stage.logs && stage.logs.length > 0 && (
                      <Card
                        size="small"
                        style={{
                          marginTop: 8,
                          backgroundColor: '#f5f5f5',
                          maxHeight: 200,
                          overflowY: 'auto'
                        }}
                      >
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {stage.logs.map((log, logIndex) => (
                            <div key={logIndex} style={{ fontSize: 12, fontFamily: 'monospace' }}>
                              {log}
                            </div>
                          ))}
                        </Space>
                      </Card>
                    )}
                  </div>
                }
                status={stage.status}
              />
            ))}
          </Steps>

          {/* Success Message */}
          {allFinished && (
            <Alert
              message="Plugin published successfully!"
              description="The plugin is now available in the Installed tab and will appear on the home page."
              type="success"
              showIcon
              style={{ marginTop: 24 }}
            />
          )}

          {/* Error Summary */}
          {hasError && !publishing && (
            <Alert
              message="Publishing failed"
              description="One or more stages failed. Click 'Retry Failed Stage' to retry from the failed stage."
              type="error"
              showIcon
              style={{ marginTop: 24 }}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default PublishDialog;

